import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { BlogPostForm } from "@/components/blog/BlogPostForm";
import { blogPostByIdQueryOptions } from "@/api-queries/blog";
import { CaretLeft } from "@phosphor-icons/react";
import { toast } from "@/lib/toast";
import { throwIfNotOk } from "@/lib/api-error";

export const Route = createFileRoute("/dashboard/admin/blog/$postId/edit")({
  component: AdminEditPost,
});

function AdminEditPost() {
  const { postId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    data: post,
    isLoading,
    error,
  } = useQuery(blogPostByIdQueryOptions(postId));

  const handleSubmit = async (data: {
    title: string;
    content: string;
    excerpt?: string;
    category: string;
    coverImageUrl?: string | null;
    status: string;
    therapistId?: string | null;
  }) => {
    setIsSubmitting(true);
    try {
      const res = await fetch(`/api/blog/${postId}/update`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      await throwIfNotOk(res, "Greška pri ažuriranju članka.");

      await queryClient.invalidateQueries({ queryKey: ["blog"] });
      navigate({ to: "/dashboard/admin/blog" });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Greška pri ažuriranju članka.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="mx-auto max-w-6xl space-y-6">
        <div>
          <div className="mb-3 h-4 w-32 animate-pulse rounded bg-muted" />
          <div className="h-8 w-48 animate-pulse rounded bg-muted" />
          <div className="mt-2 h-4 w-64 animate-pulse rounded bg-muted/60" />
        </div>
        <div className="h-52 animate-pulse rounded-xl bg-muted/40" />
        <div className="h-16 animate-pulse rounded-xl bg-muted/30" />
        <div className="h-16 animate-pulse rounded-xl bg-muted/30" />
        <div className="h-96 animate-pulse rounded-xl bg-muted/20" />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="mx-auto max-w-6xl">
        <Link
          to="/dashboard/admin/blog"
          className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <CaretLeft className="size-4" />
          Nazad na članke
        </Link>
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border/60 bg-muted/20 px-6 py-16 text-center">
          <h3
            className="text-lg font-semibold text-foreground"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            Članak nije pronađen
          </h3>
          <p className="mt-1.5 text-sm text-muted-foreground">
            Članak koji tražite ne postoji ili je obrisan.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6 sm:space-y-8">
      {/* Header */}
      <div>
        <Link
          to="/dashboard/admin/blog"
          className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <CaretLeft className="size-4" />
          Nazad na članke
        </Link>
        <h1
          className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl"
          style={{ fontFamily: "var(--font-heading)" }}
        >
          Uredi članak
        </h1>
        <p className="mt-1 text-sm text-muted-foreground sm:text-base">
          Uredite postojeći blog članak.
        </p>
      </div>

      <BlogPostForm
        initialData={post}
        userRole="admin"
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
